import React, { useEffect, useId, useState } from "react"
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts"

interface DataPoint {
  label: string
  kes: number
}

function CustomTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean
  payload?: Array<{ value: number }>
  label?: string
}) {
  if (!active || !payload?.length) return null
  return (
    <div className="rounded-lg border border-border bg-card px-3.5 py-2.5 shadow-lg text-xs min-w-[120px]">
      <p className="text-muted-foreground mb-1 text-[11px] font-medium">{label}</p>
      <p className="font-semibold text-foreground text-sm tabular-nums">
        KES {payload[0].value.toLocaleString()}
      </p>
    </div>
  )
}

function CustomCursor({ x, y, height }: { x?: number; y?: number; height?: number }) {
  if (x == null || y == null || height == null) return null
  return (
    <line
      x1={x}
      y1={y}
      x2={x}
      y2={y + height}
      stroke="currentColor"
      strokeWidth={1}
      strokeDasharray="3 3"
      className="text-border"
    />
  )
}

const readCSSColor = (prop: string, fallback: string): string => {
  if (typeof window === "undefined") return fallback
  const val = getComputedStyle(document.documentElement).getPropertyValue(prop).trim()
  // CSS custom property may be an oklch/hsl value — wrap it for inline SVG use
  return val !== "" ? `hsl(${val})` : fallback
}

// Convert oklch/hsl token to a hex string usable in SVG gradients.
// recharts gradients need a real color, not currentColor.
const readPrimary = (): string => {
  if (typeof window === "undefined") return "#6366f1"
  // Render to a temp element to resolve the computed color
  const tmp = document.createElement("div")
  tmp.className = "text-primary"
  tmp.style.cssText = "position:absolute;visibility:hidden;color:hsl(var(--primary,222 47% 55%))"
  document.body.appendChild(tmp)
  const color = getComputedStyle(tmp).color
  document.body.removeChild(tmp)
  return color || "#6366f1"
}

export function AdminRevenueChart({ data }: { data: DataPoint[] }) {
  const gradId = useId().replace(/:/g, "")
  const [primary, setPrimary] = useState<string>(() => readPrimary())

  useEffect(() => {
    const observer = new MutationObserver(() => setPrimary(readPrimary()))
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    })
    return () => observer.disconnect()
  }, [])

  const isDark =
    typeof document !== "undefined" &&
    document.documentElement.classList.contains("dark")

  const gridStroke = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"
  const tickFill = isDark ? "#71717a" : "#a1a1aa"

  const hasData = data.some((d) => d.kes > 0)

  return (
    <ResponsiveContainer width="100%" height={220}>
      <AreaChart data={data} margin={{ top: 10, right: 4, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id={`rev-${gradId}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={primary} stopOpacity={isDark ? 0.25 : 0.18} />
            <stop offset="75%" stopColor={primary} stopOpacity={0.03} />
            <stop offset="100%" stopColor={primary} stopOpacity={0} />
          </linearGradient>
        </defs>

        <CartesianGrid
          strokeDasharray="0"
          horizontal={true}
          vertical={false}
          stroke={gridStroke}
        />

        <XAxis
          dataKey="label"
          axisLine={false}
          tickLine={false}
          tick={{
            fill: tickFill,
            fontSize: 11,
            fontFamily: "system-ui, sans-serif",
          }}
          dy={8}
          interval="preserveStartEnd"
        />

        <YAxis
          axisLine={false}
          tickLine={false}
          tick={{
            fill: tickFill,
            fontSize: 11,
            fontFamily: "system-ui, sans-serif",
          }}
          tickFormatter={(v: number) =>
            v === 0 ? "0" : v >= 1_000_000
              ? `${(v / 1_000_000).toFixed(1)}M`
              : v >= 1000
              ? `${(v / 1000).toFixed(0)}k`
              : `${v}`
          }
          width={38}
          tickCount={5}
        />

        <Tooltip
          content={<CustomTooltip />}
          cursor={<CustomCursor />}
          wrapperStyle={{ outline: "none" }}
        />

        <Area
          type="monotone"
          dataKey="kes"
          stroke={primary}
          strokeWidth={hasData ? 2 : 1.5}
          fill={`url(#rev-${gradId})`}
          dot={false}
          activeDot={{
            r: 4,
            fill: primary,
            stroke: isDark ? "#18181b" : "#ffffff",
            strokeWidth: 2,
          }}
          isAnimationActive={true}
          animationDuration={600}
          animationEasing="ease-out"
        />
      </AreaChart>
    </ResponsiveContainer>
  )
}
